import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import {
  AutoAwesomeMotion as AutoAwesomeMotionIcon,
  DashboardCustomize as DashboardCustomizeIcon,
  KeyboardDoubleArrowDown as KeyboardDoubleArrowDownIcon,
  KeyboardDoubleArrowUp as KeyboardDoubleArrowUpIcon,
  Subtitles as SubtitlesIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material'
import {
  Box,
  ClickAwayListener,
  Divider,
  MenuItem,
  Select,
  Stack,
  Tooltip,
} from '@mui/material'
import _ from 'lodash'
import { ActionButton, Scrollbar, ToggleableButton } from '~/components'
import { useCanEdit, useMounted, usePersistedStorage, usePrompt } from '~/hooks'
import { useRouteContext } from '~/routes'
import { bshSlice, collapsedSlice } from '~/store'
import { compareArrays } from '~/utils'
import {
  CopyParagraphsFrom,
  CustomParagraph,
  EmpCoTools,
  Paragraph,
  ResolutionSlider,
} from './components'

const ParagraphsEditor = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const canEdit = useCanEdit()
  const { subCategory, product } = useRouteContext(ParagraphsEditor.name)

  const shops = useSelector(bshSlice.shops)
  const originalParagraphs = useSelector(bshSlice.paragraphs)
  const [paragraphs, setParagraphs] = useState([...originalParagraphs])
  useEffect(() => {
    queueMicrotask(() => {
      setParagraphs([...originalParagraphs])
    })
  }, [originalParagraphs])

  const allCollapsedIds = useMemo(
    () => paragraphs.map((p) => `${Paragraph.name}-${p.id}`),
    [paragraphs]
  )

  const allCollapsed = useSelector((state) =>
    collapsedSlice.isCollapsed(state, 'sections', allCollapsedIds)
  )

  const [preview, setPreview] = usePersistedStorage('preview', false)
  const [showCopyDialog, setShowCopyDialog] = useState(false)
  useEffect(() => {
    if (preview && showCopyDialog) {
      queueMicrotask(() => {
        setShowCopyDialog(false)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview])
  const [resolution, setResolution] = usePersistedStorage('resolution', 832)

  const allShops = useMemo(
    () => ({
      id: 0,
      name: t('paragraph.all-shops'),
      rwd: [500, 770, 1050],
    }),
    [t]
  )
  const [shopId, setShopId] = usePersistedStorage('shop-id', allShops.id)
  const shop = useMemo(() => {
    const shop = shops?.find((shop) => shop.id === shopId)
    return shop ?? allShops
  }, [allShops, shopId, shops])

  const shopRwd = shop?.rwd
  const shopRwdLength = shopRwd?.length

  const [editingParagraphs, setEditingParagraphs] = useState([])
  useEffect(() => {
    queueMicrotask(() => {
      setEditingParagraphs([])
    })
  }, [paragraphs])

  const reorderParagraphs = useCallback(
    async (ids) => {
      if (!ids) {
        ids = paragraphs.map(({ id }) => id)
      }
      await dispatch(
        bshSlice.reorderParagraphs({
          data: { owner: product ?? subCategory, ids },
        })
      )
    },
    [dispatch, paragraphs, product, subCategory]
  )

  const moveParagraph = useCallback(
    async (index, direction) => {
      const ids = paragraphs.map(({ id }) => id)
      const id = ids[index]
      if (id) {
        ids.splice(index, 1)
        index += direction
        if (index >= 0 && index <= ids.length) {
          ids.splice(index, 0, id)
        }
        await reorderParagraphs(ids)
      }
    },
    [paragraphs, reorderParagraphs]
  )

  const swapParagraphs = useCallback((srcIndex, destIndex) => {
    // const srcIndex = paragraphs.findIndex((p) => p.id === srcId)
    // const destIndex = paragraphs.findIndex((p) => p.id === destId)
    if (
      srcIndex !== undefined &&
      destIndex !== undefined &&
      srcIndex !== destIndex
    ) {
      setParagraphs((paragraphs) => {
        const newParagraphs = [...paragraphs]
        // Remove element from srcIndex
        const [removed] = newParagraphs.splice(srcIndex, 1)
        // Insert at destIndex
        newParagraphs.splice(destIndex, 0, removed)
        return newParagraphs
      })
    }
  }, [])

  usePrompt({ isDirty: editingParagraphs.length > 0 })

  const getParagraphs = useCallback(async () => {
    await dispatch(
      bshSlice.getParagraphs({ data: { owner: product ?? subCategory } })
    )
  }, [dispatch, product, subCategory])

  const mounted = useMounted()
  useEffect(() => {
    if (!mounted) {
      dispatch(bshSlice.clearParagraphs())
      return
    }
    const intId = setTimeout(getParagraphs, 0)
    return () => clearTimeout(intId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted, product, subCategory])

  const empco = useSelector(bshSlice.empCoSelected)

  const interaction = useSelector(bshSlice.interaction)

  const tools = useMemo(() => {
    return (
      <>
        <Stack
          direction="row"
          spacing={0.5}
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Stack direction="row" spacing={0.5} alignItems="center">
              {canEdit && (
                <ActionButton
                  background
                  disabled={preview || shopId !== allShops.id}
                  icon={<DashboardCustomizeIcon />}
                  title={t('paragraph.create-new')}
                  onClick={async () => {
                    await dispatch(
                      bshSlice.createParagraph({
                        data: { owner: product ?? subCategory, type: 'top' },
                      })
                    )
                  }}
                />
              )}
              <ToggleableButton
                background
                disabled={preview}
                icon={{
                  on: <KeyboardDoubleArrowDownIcon />,
                  off: <KeyboardDoubleArrowUpIcon />,
                }}
                on={allCollapsed}
                title={t(
                  allCollapsed
                    ? 'paragraph.expand-all'
                    : 'paragraph.collapse-all'
                )}
                onChange={(on) => {
                  const payload = paragraphs
                    .map((p) => p.id)
                    .map((id) => ({
                      key: 'sections',
                      id: `${Paragraph.name}-${id}`,
                      collapsed: on,
                    }))
                  dispatch(collapsedSlice.setCollapsed(payload))
                }}
              />
              {canEdit && product && (
                <>
                  <ActionButton
                    background
                    disabled={preview || shopId !== allShops.id}
                    icon={<AutoAwesomeMotionIcon />}
                    title={t('paragraph.copy.title')}
                    onClick={() => setShowCopyDialog((show) => !show)}
                  />
                  <ActionButton
                    background
                    disabled={preview || shopId !== allShops.id}
                    icon={<SubtitlesIcon />}
                    title={t('paragraph.layout')}
                    onClick={() => {
                      dispatch(
                        bshSlice.layoutParagraphs({
                          data: { product },
                        })
                      )
                    }}
                  />
                </>
              )}
            </Stack>
            {!preview && <EmpCoTools shop={shop} />}
          </Stack>
          {canEdit && !preview && (
            <Tooltip title={t('paragraph.interaction.tooltip')} placement="top">
              <Select
                value={interaction}
                variant="standard"
                onChange={(event) => {
                  dispatch(bshSlice.setInteraction(event.target.value))
                }}
              >
                <MenuItem value="buttons">
                  {t('paragraph.interaction.buttons')}
                </MenuItem>
                <MenuItem value="save">
                  {t('paragraph.interaction.save')}
                </MenuItem>
                <MenuItem value="overwrite">
                  {t('paragraph.interaction.overwrite')}
                </MenuItem>
              </Select>
            </Tooltip>
          )}
          <Stack direction="row" spacing={2} alignItems="center">
            <Stack direction="row" spacing={1} alignItems="center">
              <ToggleableButton
                background
                icon={{ on: <VisibilityOffIcon />, off: <VisibilityIcon /> }}
                on={preview}
                title={t(
                  preview ? 'paragraph.hide-preview' : 'paragraph.show-preview'
                )}
                onChange={setPreview}
              />
              <Select
                sx={{
                  padding: 0,
                  minWidth: 234,
                }}
                variant="standard"
                value={
                  shopId === allShops.id ||
                  shops?.find(({ id }) => id === shopId)
                    ? shopId
                    : ''
                }
                onChange={(event) => {
                  setShopId(event.target.value)
                }}
              >
                <MenuItem value={allShops.id}>{allShops.name}</MenuItem>
                {shops?.map((shop, index) => (
                  <MenuItem key={index} value={shop.id}>
                    {shop.name}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <ResolutionSlider
                defaultValue={resolution}
                disabled={!preview}
                marks={shopRwd?.map((value) => ({ value }))}
                onChange={setResolution}
              />
            </Stack>
          </Stack>
        </Stack>
        <Divider />
        {showCopyDialog && (
          <ClickAwayListener onClickAway={() => setShowCopyDialog(false)}>
            <Box sx={{ position: 'relative', mt: '-1rem' }}>
              <Stack
                sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  height: '100vh',
                  zIndex: 1,
                }}
              >
                <CopyParagraphsFrom />
                <Box
                  sx={{
                    backgroundColor: 'background.default',
                    flexGrow: 1,
                    opacity: 0.65,
                  }}
                  onClick={() => setShowCopyDialog(false)}
                />
              </Stack>
            </Box>
          </ClickAwayListener>
        )}
      </>
    )
  }, [
    preview,
    shopId,
    allShops.id,
    allShops.name,
    t,
    allCollapsed,
    product,
    shop,
    interaction,
    canEdit,
    setPreview,
    shops,
    resolution,
    shopRwd,
    setResolution,
    showCopyDialog,
    dispatch,
    subCategory,
    paragraphs,
    setShopId,
  ])

  const content = useMemo(() => {
    if (!mounted) return null

    let content
    let prev = 'left'

    if (!preview) {
      content = (
        <Stack spacing={3}>
          {paragraphs?.map((paragraph, index) => {
            const onEditing = (paragraph, editing) => {
              const result = [...editingParagraphs]
              const index = result.indexOf(paragraph)
              if (editing) {
                if (index < 0) {
                  result.push(paragraph)
                }
              } else if (index >= 0) {
                result.splice(index, 1)
              }
              if (!compareArrays(editingParagraphs, result)) {
                setEditingParagraphs(result)
              }
            }
            const isEmpco =
              empco &&
              !bshSlice.paragraphContains(paragraph, empco.target, shop.slug)
            if (shopId === allShops.id) {
              let paragraphType = paragraph.type
              if (!/left|right/.test(paragraphType)) {
                prev = 'left'
              } else {
                const type = prev
                  ? prev === 'left'
                    ? 'right'
                    : 'left'
                  : paragraph.type.replace(/(-)?(left|right)$/, '$2')
                if (prev && !new RegExp(type).test(paragraphType)) {
                  paragraphType = paragraphType.replace(
                    /(-)?(left|right)$/,
                    `$1${type}`
                  )
                }
                prev = type
              }
              return isEmpco ? null : (
                <Paragraph
                  key={paragraph.id}
                  draggable={canEdit && allCollapsed}
                  index={index}
                  paragraph={paragraph}
                  paragraphType={paragraphType}
                  reorderParagraphs={reorderParagraphs}
                  swapParagraphs={swapParagraphs}
                  onEditing={onEditing}
                  onMoveUp={
                    index > 0 ? moveParagraph.bind(null, index, -1) : undefined
                  }
                  onMoveDown={
                    index < paragraphs.length - 1
                      ? moveParagraph.bind(null, index, 1)
                      : undefined
                  }
                  onRemoved={() => getParagraphs()}
                />
              )
            }
            return isEmpco ? null : (
              <CustomParagraph
                key={paragraph.id}
                paragraph={paragraph}
                variant={shop.slug}
                onEditing={onEditing}
              />
            )
          })}
        </Stack>
      )
    } else {
      let className = 'wide'
      if (shopRwdLength === 3) {
        if (resolution < shopRwd[2]) className = 'desktop'
        if (resolution < shopRwd[1]) className = 'tablet'
        if (resolution < shopRwd[0]) className = 'mobile'
      }
      content = paragraphs?.map((paragraph, index) => {
        if (
          paragraph.shops?.[shop.slug]?.skip ||
          (shop.slug === 'euro' && /movie/.test(paragraph.type))
        ) {
          return null
        }

        let html = paragraph.shops?.[shop.slug]?.html ?? paragraph.html

        html = html.replace(
          '<div class="html-preview',
          '<div class="html-preview ' + className
        )
        if (!/left|right/.test(paragraph.type)) {
          prev = 'left'
        } else {
          const type = prev
            ? prev === 'left'
              ? 'right'
              : 'left'
            : paragraph.type.replace(/(-)?(left|right)$/, '$2')
          if (prev && !new RegExp(type).test(paragraph.type)) {
            const frag = document.createDocumentFragment()
            let temp = document.createElement('div')
            temp.innerHTML = html
            while (temp.firstChild) {
              frag.appendChild(temp.firstChild)
            }
            const table = frag.querySelector('.table')
            if (table) {
              table.prepend(table.querySelector('.half:last-of-type'))
            }
            frag.firstChild.classList.replace(
              paragraph.type,
              paragraph.type.replace(/(-)?(left|right)$/, `$1${type}`)
            )
            frag.firstChild.setAttribute('data-type', type)
            temp = document.createElement('div')
            temp.appendChild(frag)
            html = temp.innerHTML
          }
          prev = type
        }

        const containerClass =
          'html-container' +
          (paragraph.type === 'text' ? ' html-container-continuous' : '') +
          ' ' +
          className

        return html ? (
          <div
            key={index}
            className={containerClass}
            dangerouslySetInnerHTML={{ __html: html }}
            style={{ width: resolution }}
          />
        ) : null
      })
      content = (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            width: `${resolution}px`,
          }}
        >
          {content}
        </Box>
      )
    }

    return (
      <Box
        sx={{
          position: 'relative',
          flexGrow: 1,
          backgroundColor: preview ? '#ffffff' : undefined,
        }}
      >
        <Scrollbar
          className="absolute inset-0"
          data-testid="paragraphs-editor-scrollbar"
          native={preview}
          options={preview ? undefined : { suppressScrollX: true }}
        >
          {content}
        </Scrollbar>
      </Box>
    )
  }, [
    mounted,
    preview,
    empco,
    paragraphs,
    shopId,
    allShops.id,
    shop.slug,
    editingParagraphs,
    allCollapsed,
    canEdit,
    reorderParagraphs,
    swapParagraphs,
    moveParagraph,
    getParagraphs,
    shopRwdLength,
    resolution,
    shopRwd,
  ])

  return useMemo(
    () => (
      <Stack spacing={2} sx={{ flexGrow: 1 }}>
        {tools}
        {content}
      </Stack>
    ),
    [content, tools]
  )
}

export default ParagraphsEditor
